import React from "react";
import { Phone, MessageCircle, Star } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { buildWaLink, GENERAL_MESSAGE, serviceMessage, QUICK_CHIPS, trackWhatsApp } from "@/lib/services";
import { useI18n } from "@/lib/i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1631815587646-b85a1bb027e1?fm=jpg&q=85&w=1400&auto=format&fit=crop";
// (Fallback-friendly real-looking nurse-with-elderly photo. Replace with a Velixa photo when ready.)

export default function Hero() {
  const { t } = useI18n();
  return (
    <section data-testid="hero-section" className="relative pt-36 md:pt-44 pb-16 md:pb-24 bg-bone">
      <div className="container-x grid grid-cols-12 gap-8 md:gap-12 items-center">
        {/* Copy */}
        <div className="col-span-12 lg:col-span-6">
          <h1 data-testid="hero-headline" className="text-[36px] sm:text-[44px] md:text-[56px] font-extrabold text-navy leading-[1.08]">
            {t("hero.h1a")}<br/>
            <span className="text-brand">{t("hero.h1b")}</span>
          </h1>
          <p data-testid="hero-sub" className="mt-5 text-[18px] md:text-[20px] text-ink/85 max-w-xl leading-[1.55]">
            {t("hero.sub")}
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              data-testid="hero-wa-btn"
              href={buildWaLink(GENERAL_MESSAGE)}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackWhatsApp("Hero WhatsApp")}
              className="btn-wa"
            >
              <MessageCircle className="w-5 h-5" aria-hidden="true" /> {t("cta.book_wa")}
            </a>
            <a data-testid="hero-call-btn" href={`tel:${BRAND.phoneTel}`} className="btn-navy" aria-label={`Call ${BRAND.phoneDisplay}`}>
              <Phone className="w-5 h-5" aria-hidden="true" /> {t("cta.call_now")}
            </a>
          </div>

          <div className="mt-8">
            <div className="text-[13px] font-semibold text-navy/70 mb-3">{t("hero.what_need")}</div>
            <div className="flex flex-wrap gap-2">
              {QUICK_CHIPS.map((c, i) => (
                <a
                  key={c.label}
                  data-testid={`hero-chip-${i}`}
                  href={buildWaLink(serviceMessage(c.service))}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => trackWhatsApp(c.service)}
                  aria-label={`Chat on WhatsApp about ${c.service}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-navy/15 bg-white hover:border-care hover:text-care text-navy font-semibold text-[14px] transition-colors"
                >
                  {c.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="col-span-12 lg:col-span-6">
          <div className="relative">
            <div className="aspect-[4/5] md:aspect-[5/5] rounded-3xl overflow-hidden shadow-card bg-soft-blue">
              <img src={HERO_IMG} alt="Indian nurse caring for an elderly patient at home in Bangalore" className="w-full h-full object-cover" loading="eager" />
            </div>

            {/* Floating cards */}
            <div className="absolute -top-4 -left-4 md:-left-6 bg-white rounded-2xl shadow-card px-4 py-3 flex items-center gap-2.5 max-w-[220px]">
              <div className="flex -space-x-0.5">
                {[0,1,2,3,4].map(i => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
              </div>
              <div>
                <div className="text-[13px] font-bold text-navy leading-tight">{BRAND.rating} on Google</div>
                <div className="text-[11px] text-ink/70">{BRAND.ratingCount} reviews</div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 md:-right-6 bg-care text-white rounded-2xl shadow-card px-4 py-3 max-w-[240px]">
              <div className="text-[13px] font-bold leading-tight">Nurse-led · Available 24/7</div>
              <div className="text-[11px] opacity-90 mt-0.5">Founded by nurses from Manipal Hospitals</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
